// semeia-disable-reexport
import type Store from "@ember-data/store";
import { isTesting } from "@embroider/macros";
import type Medic from "parog-web/models/medic";
import type { SemeiaMedicSession } from "parog-web/services/session";
import PatientListFilterSerializer, {
  PatientListFilterParser,
  filterSeparator,
  keyValueSeparator
} from "parog-web/utils/patient-list-filter-serializer";
import { getItemFromLocalStorage } from "parog-web/utils/use-local-storage";
import { setItemInLocalStorage } from "./use-local-storage";

export function initialPatientListFilter(
  store: Store,
  authenticatedMedic: Medic,
  shouldUseAllCentres?: boolean
): string {
  const storedFilter = getItemFromLocalStorage(_selectedFilterKey());
  if (
    storedFilter &&
    _isValidFilter(storedFilter, store, authenticatedMedic, shouldUseAllCentres)
  ) {
    return shouldUseAllCentres
      ? storedFilter.replace("healthCentreConfiguratorId", "healthCentreId")
      : storedFilter.replace("healthCentreId", "healthCentreConfiguratorId");
  }

  return _defaultFilter(authenticatedMedic, shouldUseAllCentres);
}

export function savePatientListFilter(filter: string): void {
  setItemInLocalStorage(_selectedFilterKey(), filter);
}

function _defaultFilter(medic: Medic, shouldUseAllCentres?: boolean): string {
  let filters = {
    fur: "all",
    medicId: medic.id,
    patientStatus: "enrolled",
    taskType: "all",
    responsibleId: "all",
    tlsConsentStatus: "all"
  };

  if (shouldUseAllCentres)
    filters = Object.assign(filters, {
      healthCentreId:
        medic.healthCentres.length == 1 ? medic.healthCentres[0].id : "all"
    });
  else
    filters = Object.assign(filters, {
      healthCentreConfiguratorId:
        medic.healthCentres.length == 1 ? medic.healthCentres[0].id : "all"
    });
  return Object.entries(filters)
    .map(([key, value]) => `${key}${keyValueSeparator}${value}`)
    .join(filterSeparator);
}

function _isValidFilter(
  serializedFilter: string,
  store: Store,
  authenticatedMedic: Medic,
  shouldUseAllCentres?: boolean
): boolean {
  const parser = new PatientListFilterParser({
    serializedFilter,
    store
  });
  if (shouldUseAllCentres) {
    const selectedHc =
      parser.filters.healthCentreId ||
      parser.filters.healthCentreConfiguratorId;
    if (!selectedHc || selectedHc === "all") {
      return parser.isValid;
    }
    return authenticatedMedic.healthCentres
      .map(hc => hc.id)
      .includes(selectedHc)
      ? parser.isValid
      : false;
  }
  return parser.isValid;
}

export function _selectedFilterKey(): string {
  const key = "medics-web.protected.updates.selectedFilter";
  if (isTesting()) {
    return `test-${key}`;
  }
  return key;
}

export function computeFilter(
  selectedFilter: string | undefined,
  store: Store,
  session: SemeiaMedicSession,
  shouldUseAllCentres?: boolean
): {
  serializedFilter: string;
  filter: PatientListFilterSerializer;
} {
  const serializedFilter =
    selectedFilter ||
    initialPatientListFilter(store, session.medic, shouldUseAllCentres);
  savePatientListFilter(serializedFilter);
  const filter = new PatientListFilterSerializer({
    serializedFilter,
    store
  });
  return { serializedFilter, filter };
}
